import React, { useState, useEffect, useCallback } from 'react';
import { X, FileText, CheckCircle2, Download, Loader2 } from 'lucide-react';
import { facturasService } from '../../../services/api';

const fmtMoneda = (n, moneda = 'USD') => {
  const valor = parseFloat(n);
  if (!Number.isFinite(valor)) return moneda === 'USD' ? '$0.00' : 'Bs. 0,00';
  return moneda === 'USD' ? `$${valor.toFixed(2)}` : `Bs. ${valor.toFixed(2)}`;
};

// Cotizacion -> factura borrador (tasa/OC/moneda) -> emitir (numeros del
// talonario preimpreso) -> PDF. Cada paso solo aparece cuando el anterior
// ya quedo resuelto; si la cotizacion ya tiene una factura (borrador o
// emitida) se retoma desde ahi en vez de ofrecer crear otra.
const FacturaEmisionModal = ({ cotizacion, onClose, onEmitida }) => {
  const [cargando, setCargando] = useState(true);
  const [factura, setFactura] = useState(null);
  const [error, setError] = useState('');

  const [creando, setCreando] = useState(false);
  const [datosCreacion, setDatosCreacion] = useState({
    moneda: cotizacion.moneda || 'USD',
    tasa: '',
    tasa_fuente: 'BCV',
    oc: '',
    metodo_pago: ''
  });

  const [emitiendo, setEmitiendo] = useState(false);
  const [numeroControl, setNumeroControl] = useState('');
  const [numeroFactura, setNumeroFactura] = useState('');

  const [descargando, setDescargando] = useState(false);

  const buscarFacturaExistente = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await facturasService.getAll({ cotizacion_id: cotizacion.id });
      // Una factura anulada no cuenta como "ya facturada": se puede
      // volver a crear la factura real despues de anular un error.
      const vigente = (res.data || []).find((f) => f.estado !== 'anulada');
      setFactura(vigente || null);
    } catch (err) {
      setError(err.message || 'Error al buscar la factura de esta cotización');
    } finally {
      setCargando(false);
    }
  }, [cotizacion.id]);

  useEffect(() => { buscarFacturaExistente(); }, [buscarFacturaExistente]);

  const handleCrear = async (e) => {
    e.preventDefault();
    setCreando(true);
    setError('');
    try {
      const res = await facturasService.createDesdeCotizacion(cotizacion.id, {
        moneda: datosCreacion.moneda,
        tasa: datosCreacion.tasa ? parseFloat(datosCreacion.tasa) : undefined,
        tasa_fuente: datosCreacion.tasa_fuente || undefined,
        oc: datosCreacion.oc || undefined,
        metodo_pago: datosCreacion.metodo_pago || undefined
      });
      setFactura(res.data);
    } catch (err) {
      setError(err.message || 'Error al crear la factura');
    } finally {
      setCreando(false);
    }
  };

  const handleEmitir = async (e) => {
    e.preventDefault();
    setEmitiendo(true);
    setError('');
    try {
      const res = await facturasService.emitir(factura.id, {
        numero_control: numeroControl || undefined,
        numero_factura: numeroFactura || undefined
      });
      setFactura(res.data);
      if (onEmitida) onEmitida(res.data);
    } catch (err) {
      setError(err.message || 'Error al emitir la factura');
    } finally {
      setEmitiendo(false);
    }
  };

  const handleDescargarPdf = async () => {
    setDescargando(true);
    setError('');
    try {
      const blob = await facturasService.descargarPdf(factura.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(err.message || 'Error al descargar el PDF');
    } finally {
      setDescargando(false);
    }
  };

  const yaEmitida = factura && factura.estado !== 'borrador';
  const subtotalItems = (factura?.items || []).reduce((sum, it) => sum + (parseFloat(it.subtotal) || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <FileText size={18} className="text-blue-600" />
            Facturar {cotizacion.numero_cotizacion || cotizacion.numero}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          )}

          {cargando ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : !factura ? (
            <form onSubmit={handleCrear} className="space-y-4">
              <p className="text-sm text-gray-600">
                Todavía no hay factura para esta cotización. Completa los datos y se crea en borrador
                (los ítems y precios de venta se copian de la cotización).
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Moneda</label>
                  <select
                    value={datosCreacion.moneda}
                    onChange={(e) => setDatosCreacion(p => ({ ...p, moneda: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="VES">VES</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tasa de cambio (Bs. por $)</label>
                  <input
                    type="number" step="0.0001" min="0"
                    value={datosCreacion.tasa}
                    onChange={(e) => setDatosCreacion(p => ({ ...p, tasa: e.target.value }))}
                    placeholder="Ej: 45.50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fuente de la tasa</label>
                  <select
                    value={datosCreacion.tasa_fuente}
                    onChange={(e) => setDatosCreacion(p => ({ ...p, tasa_fuente: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BCV">BCV</option>
                    <option value="paralelo">Paralelo</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Orden de compra (si tiene)</label>
                  <input
                    type="text"
                    value={datosCreacion.oc}
                    onChange={(e) => setDatosCreacion(p => ({ ...p, oc: e.target.value }))}
                    placeholder="N° de OC del cliente"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Método de pago</label>
                  <input
                    type="text"
                    value={datosCreacion.metodo_pago}
                    onChange={(e) => setDatosCreacion(p => ({ ...p, metodo_pago: e.target.value }))}
                    placeholder="Ej: Transferencia, efectivo, Zelle..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creando}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {creando ? 'Creando…' : 'Crear factura (borrador)'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg border p-3 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Estado</span><span className="font-medium">{factura.estado}</span></div>
                {factura.oc && <div className="flex justify-between"><span className="text-gray-500">O/C</span><span className="font-medium">{factura.oc}</span></div>}
                {factura.tasa && <div className="flex justify-between"><span className="text-gray-500">Tasa</span><span className="font-medium">{fmtMoneda(factura.tasa, 'VES')} / $ ({factura.tasa_fuente})</span></div>}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <th className="px-3 py-2 text-left">Artículo</th>
                      <th className="px-3 py-2 text-center">Cant.</th>
                      <th className="px-3 py-2 text-right">P. Unit.</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(factura.items || []).map((it) => (
                      <tr key={it.id} className="border-t border-gray-100">
                        <td className="px-3 py-2">{it.descripcion}</td>
                        <td className="px-3 py-2 text-center">{parseFloat(it.cantidad)}</td>
                        <td className="px-3 py-2 text-right">{fmtMoneda(it.precio_unitario, factura.moneda)}</td>
                        <td className="px-3 py-2 text-right">{fmtMoneda(it.subtotal, factura.moneda)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="border-t p-3 text-sm space-y-1">
                  {factura.estado === 'borrador' ? (
                    <>
                      {/* IVA/total reales los calcula fn_emitir_factura con la alicuota
                          vigente al emitir; en borrador solo se puede mostrar la suma
                          de los items, no inventar un IVA antes de tiempo. */}
                      <div className="flex justify-between"><span className="text-gray-500">Subtotal (ítems)</span><span>{fmtMoneda(subtotalItems, factura.moneda)}</span></div>
                      <p className="text-xs text-gray-400">IVA y total se calculan al emitir, con la alícuota vigente.</p>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmtMoneda(factura.subtotal, factura.moneda)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">IVA</span><span>{fmtMoneda(factura.impuesto, factura.moneda)}</span></div>
                      <div className="flex justify-between font-bold text-base"><span>Total</span><span>{fmtMoneda(factura.total, factura.moneda)}</span></div>
                    </>
                  )}
                </div>
              </div>

              {!yaEmitida ? (
                <form onSubmit={handleEmitir} className="space-y-3 border-t pt-4">
                  <p className="text-sm text-gray-600">
                    Números del talonario preimpreso. Si los dejas vacíos, el número de factura sale del correlativo interno.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">N° de Control</label>
                      <input
                        type="text"
                        value={numeroControl}
                        onChange={(e) => setNumeroControl(e.target.value)}
                        placeholder="Ej: 00-012345"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">N° de Factura</label>
                      <input
                        type="text"
                        value={numeroFactura}
                        onChange={(e) => setNumeroFactura(e.target.value)}
                        placeholder="Ej: 001234"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={emitiendo}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                    >
                      {emitiendo ? 'Emitiendo…' : 'Emitir factura'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                    <CheckCircle2 size={18} />
                    Factura {factura.numero_factura} emitida
                    {factura.numero_control && ` (control ${factura.numero_control})`}
                  </div>
                  <button
                    onClick={handleDescargarPdf}
                    disabled={descargando}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {descargando ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                    {descargando ? 'Generando…' : 'Descargar PDF de la factura'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacturaEmisionModal;
